import {
  Button,
  Grid,
  IconButton,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Tooltip
} from '@mui/material'
import { Field, FieldArray, Form, Formik } from 'formik'
import { TextField } from 'formik-mui'
import AddBoxIcon from '@mui/icons-material/AddBox'
import React, { Fragment, useState, useEffect, useRef } from 'react'
import { DeleteIconButton } from '../../Buttons/DeleteIconButton'
import { dunValidationSchema } from '../../StockKeepingUnitDun/StockKeepingUnitDunConfig'
import {
  PreInboundReason,
  PreInboundReasonsGrid,
  WorkOrderItems
} from '../types'

export interface ILocalDunManagerProps {
  onCloseModal: () => void
  handleSnackbar: (message: string, success: boolean) => void
  onListChange?: (list: PreInboundReasonsGrid[]) => void
  workOrderItems?: WorkOrderItems
  preInboundReasonlist?: PreInboundReason[]
  listReasons: PreInboundReasonsGrid[]
  setListReasons: React.Dispatch<React.SetStateAction<PreInboundReasonsGrid[]>>
}

export const AddReason = ({
  handleSnackbar,
  onListChange,
  workOrderItems,
  preInboundReasonlist,
  onCloseModal,
  listReasons,
  setListReasons
}: ILocalDunManagerProps) => {
  const getReasonNameById = (preInboundReasonId: number): string => {
    const reason = preInboundReasonlist?.find(
      (r) => r.preInboundReasonId === preInboundReasonId
    )
    return reason?.description || ''
  }

  // Estado temporal local para los motivos mientras se editan
  const [tempReasons, setTempReasons] = useState<PreInboundReasonsGrid[]>(
    listReasons.filter(
      (reason) => reason.orderDetailId === workOrderItems?.orderDetailId
    )
  )
  const isInitialized = useRef(false)

  // useEffect para agregar un registro por defecto al cargar solo si la lista esta vacía
  useEffect(() => {
    if (!isInitialized.current && tempReasons.length === 0) {
      const defaultReasonId = 15
      const defaultReason: PreInboundReasonsGrid = {
        orderDetailId: (workOrderItems && workOrderItems.orderDetailId) || 0,
        sku: (workOrderItems && workOrderItems.sku) || '',
        quantity: 1,
        preInboundReasonId: defaultReasonId,
        preInboundReasonName: getReasonNameById(defaultReasonId)
      }
      setTempReasons([defaultReason])
      isInitialized.current = true
    }
  }, [])

  // Función para calcular el total de cantidades
  const calculateTotalQuantity = (list: PreInboundReasonsGrid[]): number => {
    return list.reduce((total, item) => total + (item.quantity || 0), 0)
  }

  // Validación personalizada para la cantidad total
  const validateTotalQuantity = (values: PreInboundReasonsGrid[]): boolean => {
    const totalQuantity = calculateTotalQuantity(values)
    const maxQuantityAllowed = workOrderItems?.expectedToReturnQuantity || 0
    return totalQuantity <= maxQuantityAllowed
  }

  // Validación para saber si se puede agregar una nueva fila
  const canAddNewRow = (values: PreInboundReasonsGrid[]): boolean => {
    const totalQuantity = calculateTotalQuantity(values)
    const maxQuantityAllowed = workOrderItems?.expectedToReturnQuantity || 0
    return totalQuantity < maxQuantityAllowed
  }

  const handleSubmit = (data: PreInboundReasonsGrid[]) => {
    if (data.length > 0 && validateTotalQuantity(data)) {
      // Filtrar los motivos existentes que NO pertenecen al workOrderDetailId actual
      const otherReasons = listReasons.filter(
        (reason) => reason.orderDetailId !== workOrderItems?.orderDetailId
      )

      // Combinar con los nuevos motivos del estado temporal
      const updatedListReasons = [...otherReasons, ...data]

      setListReasons([...updatedListReasons])
      onListChange?.(updatedListReasons)
      handleSnackbar('Lista actualizada correctamente.', true)
      onCloseModal()
    } else if (!validateTotalQuantity(data)) {
      const maxQuantityAllowed = workOrderItems?.expectedToReturnQuantity || 0
      handleSnackbar(
        `La cantidad total no puede exceder ${maxQuantityAllowed}`,
        false
      )
    } else {
      handleSnackbar('Debe agregar al menos un motivo', false)
    }
  }

  const handleDelete = (index: number, remove: (index: number) => void) => {
    remove(index)
    handleSnackbar('Motivo eliminado correctamente', true)
  }

  const addNewReason = (
    push: (item: PreInboundReasonsGrid) => void,
    values: PreInboundReasonsGrid[]
  ) => {
    if (!canAddNewRow(values)) {
      const maxQuantityAllowed = workOrderItems?.expectedToReturnQuantity || 0
      handleSnackbar(
        `No se pueden agregar más filas. Límite de cantidad: ${maxQuantityAllowed}`,
        false
      )
      return
    }

    const defaultReasonId = 15
    const newReason: PreInboundReasonsGrid = {
      orderDetailId: (workOrderItems && workOrderItems.orderDetailId) || 0,
      sku: (workOrderItems && workOrderItems.sku) || '',
      quantity: 1,
      preInboundReasonId: defaultReasonId,
      preInboundReasonName: getReasonNameById(defaultReasonId)
    }

    push(newReason)
  }

  // Validación personalizada para el campo cantidad
  const validateQuantityField = (
    value: number,
    index: number,
    values: PreInboundReasonsGrid[]
  ): string | undefined => {
    // Primero verificar si es un número válido y mayor a 0
    if (!value || isNaN(value) || value <= 0) {
      return 'La cantidad debe ser mayor a 0'
    }

    // Verificar que sea un número entero positivo
    if (!Number.isInteger(value)) {
      return 'La cantidad debe ser un número entero'
    }

    // Calcular total excluyendo el campo actual
    const otherItemsTotal = values.reduce((total, item, i) => {
      if (i !== index) {
        return total + (item.quantity || 0)
      }
      return total
    }, 0)

    if (
      otherItemsTotal + value >
      (workOrderItems?.expectedToReturnQuantity || 0)
    ) {
      return `Total excede el límite de ${
        workOrderItems?.expectedToReturnQuantity || 0
      }`
    }

    return undefined
  }

  return (
    <React.Fragment>
      <Formik
        initialValues={{ listReasons: tempReasons }}
        enableReinitialize
        onSubmit={(values, { setSubmitting }) => {
          handleSubmit(values.listReasons)
          setSubmitting(false)
        }}
        validationSchema={dunValidationSchema}
      >
        {({ values, setFieldValue, isValid }) => {
          const totalQuantity = calculateTotalQuantity(values.listReasons)
          const isQuantityValid = validateTotalQuantity(values.listReasons)
          const maxQuantityAllowed =
            workOrderItems?.expectedToReturnQuantity || 0

          return (
            <Form autoComplete='off'>
              <FieldArray name='listReasons'>
                {({ push, remove }) => (
                  <Fragment>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <Typography variant='body2'>
                          SKU: {workOrderItems?.sku}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant='body2'>
                          Nombre: {workOrderItems?.productName}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography
                          variant='body2'
                          color={isQuantityValid ? 'textSecondary' : 'error'}
                        >
                          Cantidad total: {totalQuantity} / {maxQuantityAllowed}
                        </Typography>
                      </Grid>
                    </Grid>
                    <Grid
                      container
                      direction='row'
                      alignItems='center'
                      justifyContent='center'
                    >
                      <Grid item xs={12} style={{ textAlign: 'right' }}>
                        <Tooltip title='Agregar Motivo'>
                          <IconButton
                            aria-label='añadir motivo'
                            disabled={
                              !canAddNewRow(values.listReasons) ||
                              values.listReasons.length >= 9
                            }
                            onClick={() =>
                              addNewReason(push, values.listReasons)
                            }
                            color='primary'
                          >
                            <AddBoxIcon style={{ fontSize: '2rem' }} />
                          </IconButton>
                        </Tooltip>
                      </Grid>
                    </Grid>
                    {values.listReasons.map((_, index) => (
                      <Grid
                        container
                        item
                        key={index}
                        spacing={2}
                        direction='row'
                        justifyContent='center'
                        style={{ marginBottom: '16px' }}
                      >
                        <Grid item xs={12} sm={6} md={3}>
                          <div>
                            <Typography variant='body2'>Cantidad:</Typography>
                            <Field
                              variant='outlined'
                              fullWidth
                              name={`listReasons[${index}].quantity`}
                              component={TextField}
                              type='number'
                              validate={(value: number) =>
                                validateQuantityField(
                                  value,
                                  index,
                                  values.listReasons
                                )
                              }
                            />
                          </div>
                        </Grid>
                        <Grid item xs={12} sm={6} md={6}>
                          <div>
                            <Typography variant='body2'>Motivo:</Typography>
                            <FormControl fullWidth>
                              <Field
                                name={`listReasons[${index}].preInboundReasonId`}
                                render={({ field }: any) => (
                                  <Select
                                    variant='outlined'
                                    {...field}
                                    value={field.value || 15} // 15 = sin razón
                                    onChange={(e) => {
                                      const selectedId = e.target
                                        .value as number
                                      const selectedName =
                                        getReasonNameById(selectedId)

                                      // Actualizar tanto el ID como el nombre
                                      setFieldValue(
                                        `listReasons[${index}].preInboundReasonId`,
                                        selectedId
                                      )
                                      setFieldValue(
                                        `listReasons[${index}].preInboundReasonName`,
                                        selectedName
                                      )
                                    }}
                                  >
                                    {preInboundReasonlist?.map((reason) => (
                                      <MenuItem
                                        key={reason.preInboundReasonId}
                                        value={reason.preInboundReasonId}
                                      >
                                        {reason.description}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                )}
                              />
                            </FormControl>
                          </div>
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          sm={12}
                          md={3}
                          style={{
                            textAlign: 'center',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <DeleteIconButton
                            isDisabled={false}
                            onClick={() => handleDelete(index, remove)}
                          />
                        </Grid>
                      </Grid>
                    ))}
                    <Grid container spacing={0}>
                      <Grid item xs={12}>
                        <Button
                          disabled={
                            !isValid ||
                            values.listReasons.length <= 0 ||
                            !isQuantityValid
                          }
                          fullWidth
                          type='submit'
                          variant='contained'
                          color={
                            isValid && isQuantityValid ? 'primary' : 'secondary'
                          }
                          style={{
                            marginTop: '10px'
                          }}
                        >
                          Guardar
                        </Button>
                      </Grid>
                    </Grid>
                  </Fragment>
                )}
              </FieldArray>
            </Form>
          )
        }}
      </Formik>
    </React.Fragment>
  )
}
